/**
 * @boli/core - Product Service
 *
 * Platform-agnostic product management for Boli Live fixed-price listings.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 */

import type {
  Product,
  ProductStatus,
  ProductQueryParams,
  ProductListResponse,
  CreateProductRequest,
  UpdateProductRequest,
  ApiResponse,
  AuctionCategory,
  ProductCondition,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IProductService {
  /**
   * Get a list of products with optional filters
   */
  getProducts(params?: ProductQueryParams): Promise<ProductListResponse>;

  /**
   * Get a single product by ID
   */
  getProduct(id: string): Promise<Product | null>;

  /**
   * Create a new product (requires authentication)
   */
  createProduct(data: CreateProductRequest): Promise<ApiResponse<Product>>;

  /**
   * Update an existing product (requires authentication, owner only)
   */
  updateProduct(id: string, data: UpdateProductRequest): Promise<ApiResponse<Product>>;

  /**
   * Delete/Archive a product (requires authentication, owner only)
   */
  deleteProduct(id: string): Promise<ApiResponse<void>>;

  /**
   * Get products by seller ID
   */
  getProductsBySeller(sellerId: string, params?: ProductQueryParams): Promise<ProductListResponse>;
}

export interface ProductServiceConfig {
  apiBaseUrl: string;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_PRODUCT_CONFIG: ProductServiceConfig = {
  apiBaseUrl: '',
};

// ============================================================================
// PRODUCT SERVICE IMPLEMENTATION
// ============================================================================

export class ProductService implements IProductService {
  private config: ProductServiceConfig;
  protected fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  protected getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<ProductServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_PRODUCT_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Build query string from params
   */
  private buildQueryString(params?: ProductQueryParams): string {
    if (!params) return '';

    const queryParts: string[] = [];

    if (params.sellerId) queryParts.push(`sellerId=${encodeURIComponent(params.sellerId)}`);
    if (params.category) queryParts.push(`category=${encodeURIComponent(params.category)}`);
    if (params.subCategory) queryParts.push(`subCategory=${encodeURIComponent(params.subCategory)}`);
    if (params.condition) queryParts.push(`condition=${encodeURIComponent(params.condition)}`);
    if (params.status) queryParts.push(`status=${encodeURIComponent(params.status)}`);
    if (params.minPrice !== undefined) queryParts.push(`minPrice=${params.minPrice}`);
    if (params.maxPrice !== undefined) queryParts.push(`maxPrice=${params.maxPrice}`);
    if (params.sort) queryParts.push(`sort=${encodeURIComponent(params.sort)}`);
    if (params.limit !== undefined) queryParts.push(`limit=${params.limit}`);
    if (params.offset !== undefined) queryParts.push(`offset=${params.offset}`);

    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  /**
   * Get authenticated headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Get a list of products with optional filters
   */
  async getProducts(params?: ProductQueryParams): Promise<ProductListResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/products${queryString}`
      );

      if (!response.ok) {
        return {
          products: [],
          total: 0,
          limit: params?.limit || 50,
          offset: params?.offset || 0,
        };
      }

      const data = await response.json();
      return {
        products: data.products || [],
        total: data.total || 0,
        limit: data.limit || params?.limit || 50,
        offset: data.offset || params?.offset || 0,
      };
    } catch (error) {
      console.error('Failed to fetch products:', error);
      return {
        products: [],
        total: 0,
        limit: params?.limit || 50,
        offset: params?.offset || 0,
      };
    }
  }

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Product | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/products/${id}`
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get product:', error);
      return null;
    }
  }

  /**
   * Create a new product
   */
  async createProduct(data: CreateProductRequest): Promise<ApiResponse<Product>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/products`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'CREATE_FAILED',
            message: responseData.message || 'Failed to create product',
          },
        };
      }

      return {
        success: true,
        data: responseData.product,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Update an existing product
   */
  async updateProduct(id: string, data: UpdateProductRequest): Promise<ApiResponse<Product>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/products/${id}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(data),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'UPDATE_FAILED',
            message: responseData.message || 'Failed to update product',
          },
        };
      }

      return {
        success: true,
        data: responseData.product,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Delete/Archive a product
   */
  async deleteProduct(id: string): Promise<ApiResponse<void>> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/products/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'DELETE_FAILED',
            message: responseData.message || 'Failed to delete product',
          },
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get products by seller ID
   */
  async getProductsBySeller(
    sellerId: string,
    params?: ProductQueryParams
  ): Promise<ProductListResponse> {
    return this.getProducts({ ...params, sellerId });
  }
}

// ============================================================================
// PRODUCT SERVICE FACTORY
// ============================================================================

let productServiceInstance: ProductService | null = null;

export function createProductService(
  config: Partial<ProductServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): ProductService {
  productServiceInstance = new ProductService(config, fetchAdapter, getAuthToken);
  return productServiceInstance;
}

export function getProductService(): ProductService {
  if (!productServiceInstance) {
    throw new Error('ProductService not initialized. Call createProductService first.');
  }
  return productServiceInstance;
}
